import { useMemo, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useSuppliers } from '../../hooks/useSuppliers'
import { useAllSpecialReports, getDownloadUrl } from '../../hooks/useSupplierSpecialReports'
import type { SupplierSpecialReport } from '../../types'
import type { Supplier } from '../../types'
import { toErrorMessage } from '../../utils/toErrorMessage'
import { EsPageHeader } from './ui'
import {
  matchesProcurementTypeFilter,
  type ProcurementTypeFilter,
} from './esPortfolioFilters'
import { ProcurementTypeSelect } from './ProcurementTypeSelect'
import { supplierDisplayName } from './supplierRollup'

function contentTypeLabel(contentType: string | null): string {
  if (!contentType) return 'File'
  if (contentType.includes('pdf')) return 'PDF'
  if (contentType.includes('word') || contentType.includes('document')) return 'Word'
  if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'PowerPoint'
  return contentType
}

function formatSize(bytes: number | null): string {
  if (bytes == null || bytes === 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString(undefined, { dateStyle: 'medium' })
  } catch {
    return iso
  }
}

function fileIcon(contentType: string | null): string {
  if (!contentType) return 'fa-file'
  if (contentType.includes('pdf')) return 'fa-file-pdf'
  if (contentType.includes('word') || contentType.includes('document')) return 'fa-file-word'
  if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'fa-file-powerpoint'
  return 'fa-file'
}

function reportMatchesSearch(
  report: SupplierSpecialReport,
  supplier: Supplier | undefined,
  q: string
): boolean {
  if (report.file_name.toLowerCase().includes(q)) return true
  if (!supplier) return false
  if (supplierDisplayName(supplier).toLowerCase().includes(q)) return true
  if ((supplier.name ?? '').toLowerCase().includes(q)) return true
  if ((supplier.parent_name ?? '').toLowerCase().includes(q)) return true
  if ((supplier.supplier_unique_id ?? '').toLowerCase().includes(q)) return true
  return false
}

async function saveReportFile(report: SupplierSpecialReport): Promise<void> {
  const signed = await getDownloadUrl(report.file_path)
  if (!signed) throw new Error('Could not download this report.')
  const response = await fetch(signed)
  if (!response.ok) throw new Error('Could not download this report.')
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = report.file_name
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(objectUrl)
}

function ReportRow({ report }: { report: SupplierSpecialReport }) {
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState('')

  const handleDownload = async () => {
    setDownloading(true)
    setError('')
    try {
      await saveReportFile(report)
    } catch (err) {
      setError(toErrorMessage(err, 'Could not download this report.'))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="flex items-center justify-between gap-3 py-2.5 px-3 rounded-[var(--radius-md)] hover:bg-es-haze">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <i className={`fa-solid ${fileIcon(report.content_type)} text-es-muted text-lg shrink-0`} aria-hidden />
        <div className="min-w-0">
          <div className="font-medium text-es-navy truncate">{report.file_name}</div>
          <div className="text-xs text-es-muted">
            {contentTypeLabel(report.content_type)} · {formatSize(report.file_size)} · {formatDate(report.created_at)}
          </div>
          {error && <p className="text-xs text-red-700 mt-1">{error}</p>}
        </div>
      </div>
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        className="shrink-0 rounded-full bg-es-signal text-es-white px-3.5 py-1.5 text-sm font-semibold disabled:opacity-50"
        aria-label={`Download ${report.file_name}`}
      >
        {downloading ? (
          <i className="fa-solid fa-spinner fa-spin" aria-hidden />
        ) : (
          <>
            <i className="fa-solid fa-download mr-1.5" aria-hidden />
            Download
          </>
        )}
      </button>
    </div>
  )
}

export function SpecialReportsView() {
  const { isAdmin } = useAuth()
  const { suppliers, loading: suppliersLoading, error: suppliersError } = useSuppliers(
    isAdmin ? { scope: 'all' } : undefined
  )
  const { reports, loading: reportsLoading, error: reportsError } = useAllSpecialReports()
  const [search, setSearch] = useState('')
  const [procurementType, setProcurementType] = useState<ProcurementTypeFilter>('all')

  const supplierMap = useMemo(() => new Map(suppliers.map((s: Supplier) => [s.id, s])), [suppliers])
  const reportGroups = useMemo(() => {
    const map = new Map<string, { label: string; reports: SupplierSpecialReport[] }>()
    for (const r of reports) {
      const supplier = supplierMap.get(r.supplier_id)
      if (!matchesProcurementTypeFilter(supplier?.procurement_type, procurementType)) continue
      const label = (supplier ? supplierDisplayName(supplier) : '') || 'Unknown supplier'
      const parentKey =
        supplier?.procurement_type === 'indirect'
          ? (supplier.parent_name || '').trim().toLowerCase()
          : ''
      const key = parentKey ? `parent:${parentKey}` : `supplier:${r.supplier_id}`
      const existing = map.get(key)
      if (existing) existing.reports.push(r)
      else map.set(key, { label, reports: [r] })
    }
    return [...map.values()]
      .map((g) => ({
        ...g,
        reports: [...g.reports].sort((a, b) => a.file_name.localeCompare(b.file_name)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
  }, [reports, supplierMap, procurementType])

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return reportGroups
    return reportGroups
      .map((g) => {
        const labelHit = g.label.toLowerCase().includes(q)
        const matchedReports = labelHit
          ? g.reports
          : g.reports.filter((r) => reportMatchesSearch(r, supplierMap.get(r.supplier_id), q))
        if (matchedReports.length === 0) return null
        return { ...g, reports: matchedReports }
      })
      .filter((g): g is (typeof reportGroups)[number] => g != null)
  }, [reportGroups, search, supplierMap])

  const loading = suppliersLoading || reportsLoading
  const error = suppliersError ?? reportsError
  const selectClass =
    'rounded-[var(--radius-sm)] border border-es-hairline px-2.5 py-2 text-sm bg-es-white text-es-body focus:border-es-signal focus:outline-none focus:ring-1 focus:ring-es-signal'

  return (
    <div className="p-4 md:p-6">
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm mb-4" role="alert">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden />
          {toErrorMessage(error, 'Failed to load data.')}
        </div>
      )}
      <EsPageHeader
        title="Special reports"
        description="Download special reports (PDF, Word, PowerPoint) for each supplier. Open the file in Word, PowerPoint, or a PDF reader."
        eyebrow="Reports"
      />

      {loading ? (
        <div className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-8 text-center text-es-muted">
          <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
          Loading...
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-8 text-center">
          <p className="text-es-navy font-medium mb-2">No special reports yet.</p>
          <p className="text-sm text-es-muted max-w-md mx-auto">
            Admins can upload reports in Admin dashboard → Early Signals → Special reports.
          </p>
        </div>
      ) : (
        <div className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] overflow-hidden mb-6">
          <div className="flex flex-wrap gap-2 p-3 border-b border-es-soft bg-es-haze/80">
            <input
              type="search"
              placeholder="Search name or supplier ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`${selectClass} min-w-[180px] flex-1`}
              aria-label="Search name or supplier ID"
            />
            <ProcurementTypeSelect
              value={procurementType}
              onChange={setProcurementType}
              id="special-reports-procurement"
              showLabel={false}
            />
          </div>
          {filteredGroups.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-es-navy font-medium mb-2">
                {search.trim()
                  ? `No reports match “${search.trim()}”.`
                  : 'No reports for this Direct / Indirect filter.'}
              </p>
              <p className="text-sm text-es-muted">
                {search.trim()
                  ? 'Try another name, supplier ID, or file name.'
                  : 'Switch portfolio to Both, Direct, or Indirect.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-es-soft">
              {filteredGroups.map((group) => (
                <div key={group.label} className="p-4">
                  <h2 className="text-base font-semibold text-es-navy mb-2">{group.label}</h2>
                  <div className="divide-y divide-es-soft">
                    {group.reports.map((report) => (
                      <ReportRow key={report.id} report={report} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
